import { Button } from '@consta/uikit/Button'
import { Modal } from '@consta/uikit/Modal'
import { Table } from '@consta/uikit/Table'
import { Text } from '@consta/uikit/Text'
import { FC, useEffect, useState } from 'react'
import { IconClose } from '@consta/icons/IconClose'
import { useLazyGetWellCategoryQuery, usePostWellCategoryMutation } from '../../../services/miniportService'
import { useLazyGetProjectUuidQuery } from '../../../services/projectService'

type MonitoringStage2 = {
    isOpen: boolean,
    setIsOpen: (isOpen: boolean) => void
    handleCloseModal: () => void;
    projectUuid?: string;
}

type RowMonitoring3 = {
    one: string;
    two: string;
    three: string;
};

const MonitoringStage2: FC<MonitoringStage2> = ({
    isOpen, 
    setIsOpen, 
    handleCloseModal,
    projectUuid 
}) => {
    const [triggerGetWellCategory, { data: wellCategoryData, isLoading, isError }] = useLazyGetWellCategoryQuery();
    const [postWellCategory, { isLoading: isPosting }] = usePostWellCategoryMutation();
    const [getProject, {data: project}] = useLazyGetProjectUuidQuery()
    const dateStart= new Date();

    const yearStart = dateStart.getFullYear();
    const monthStart = (dateStart.getMonth() + 1).toString().padStart(2, '0');
    const dayStart = dateStart.getDate().toString().padStart(2, '0');

    const formattedDateStart = `${yearStart}-${monthStart}-${dayStart}`;

    useEffect(() => {
        getProject({uuid: projectUuid})
    },[projectUuid])
    
    const [rowsMonitoring3, setRowsMonitoring3] = useState<RowMonitoring3[]>([]);
    const [totalWells, setTotalWells] = useState<number>(0); // Добавляем состояние для общего количества скважин
    const [dataVersion, setDataVersion] = useState(0); // Для принудительного обновления

    // Загружаем данные при открытии модалки
    useEffect(() => {
        if (isOpen && projectUuid) {
            console.log('🔄 Загрузка данных по категориям скважин для проекта:', projectUuid);
            
            // Принудительно сбрасываем данные перед загрузкой
            setRowsMonitoring3([]);
            setTotalWells(0);
            
            triggerGetWellCategory({ project_uuid: projectUuid }, true)
                .unwrap()
                .then((data) => {
                    console.log('✅ Данные успешно загружены:', data);
                })
                .catch((err) => {
                    console.error('❌ Ошибка загрузки данных:', err);
                });
        }
    }, [isOpen, projectUuid, triggerGetWellCategory, dataVersion]);

    // Преобразуем данные из API в формат таблицы
    useEffect(() => {
        if (wellCategoryData && isOpen) {
            console.log('✅ Получены данные по категориям скважин:', wellCategoryData);
            
            let transformedRows: RowMonitoring3[] = [];
            let total = 0;
            
            // Правильная обработка данных от бэкенда
            if (wellCategoryData.wells_data && Array.isArray(wellCategoryData.wells_data)) {
                transformedRows = wellCategoryData.wells_data.map((item: any) => {
                    const count = item.count || 0;
                    total += count; // Суммируем количество скважин
                    
                    return {
                        one: item.well_category || 'Не указана',
                        two: item.layers || 'Не указан',
                        three: count.toString()
                    };
                });
            } 
            // Остальные условия для обратной совместимости
            else if (Array.isArray(wellCategoryData)) {
                transformedRows = wellCategoryData.map((item: any) => {
                    const count = item.count || 0;
                    total += count;
                    
                    return {
                        one: item.well_category || 'Не указана',
                        two: item.layers || 'Не указан',
                        three: count.toString()
                    };
                });
            } else if (typeof wellCategoryData?.wells_data === 'object' && wellCategoryData?.wells_data !== null) {
                transformedRows = Object.entries(wellCategoryData?.wells_data).map(([key, value]: [string, any]) => {
                    const count = value.count || 0;
                    total += count;
                    
                    return {
                        one: value.well_category || key || 'Не указана',
                        two: value.layers || 'Не указан',
                        three: count.toString()
                    };
                });
            }
            
            console.log('📊 Преобразованные строки:', transformedRows);
            console.log('🔢 Общее количество скважин:', total);
            
            setRowsMonitoring3(transformedRows);
            setTotalWells(total);
        } else if (!isOpen) {
            // Очищаем данные при закрытии модалки
            setRowsMonitoring3([]);
            setTotalWells(0);
        }
    }, [wellCategoryData, isOpen]);

    // Функция для принудительного обновления данных
    const handleRefreshData = () => {
        if (projectUuid) {
            console.log('🔄 Принудительное обновление данных');
            setDataVersion(prev => prev + 1);
        }
    };

    // Функция для определения типа контента и создания правильного Blob
    const createBlobFromResponse = (data: any): { blob: Blob; filename: string; isExcel: boolean } => {

        // Если это уже Blob
        if (data instanceof Blob) {
            const isExcel = data.type.includes('spreadsheet') || data.type.includes('excel');
            return {
                blob: data,
                filename: `Отчет_по_категориям_скважин_${formattedDateStart}_${project?.name}.${isExcel ? 'xlsx' : 'bin'}`,
                isExcel
            };
        }

        // Если это строка, проверяем на различные форматы
        if (typeof data === 'string') {
            // Проверяем, это JSON или бинарные данные
            try {
                // Пробуем распарсить как JSON
                const jsonData = JSON.parse(data);
                console.log('📊 Данные в JSON формате:', jsonData);
                
                // Если это JSON с ошибкой
                if (jsonData.error || jsonData.detail) {
                    throw new Error(jsonData.error || jsonData.detail || 'Ошибка сервера');
                }
                
                // Создаем CSV из данных
                return createCSVFromData();
            } catch (jsonError) {
                console.log('📄 Данные не JSON, проверяем бинарные форматы...');
                
                // Проверяем сигнатуры файлов
                const trimmedData = data.trim();
                
                // Excel файл начинается с PK (zip архив)
                if (trimmedData.startsWith('PK')) {
                    console.log('📗 Обнаружен Excel файл (ZIP сигнатура)');
                    const binaryString = atob(trimmedData);
                    const bytes = new Uint8Array(binaryString.length);
                    for (let i = 0; i < binaryString.length; i++) {
                        bytes[i] = binaryString.charCodeAt(i);
                    }
                    return {
                        blob: new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
                        filename: `well_category_${projectUuid}_${new Date().toISOString().split('T')[0]}.xlsx`,
                        isExcel: true
                    };
                }
                
                // Простой текст или CSV
                console.log('📝 Обнаружен текстовый формат, создаем CSV');
                return createCSVFromData();
            }
        }

        // Если это объект (скорее всего JSON)
        if (typeof data === 'object' && data !== null) {
            console.log('📊 Данные в объектном формате:', data);
            return createCSVFromData();
        }

        // Fallback - создаем CSV из имеющихся данных
        console.log('🔄 Неизвестный формат, создаем CSV из данных таблицы');
        return createCSVFromData();
    };

    // Создание CSV из данных таблицы
    const createCSVFromData = (): { blob: Blob; filename: string; isExcel: boolean } => {
        const headers = ['Категория скважин', 'Пласт', 'Количество скважин'];
        
        // Добавляем итоговую строку с общим количеством
        const footerRow = ['ИТОГО:', '', totalWells.toString()];
        
        const csvData = [
            // BOM для правильной кодировки UTF-8 в Excel
            '\uFEFF' + headers.join(';'),
            ...rowsMonitoring3.map(row => 
                [row.one, row.two, row.three]
                    .map(field => {
                        // Экранируем специальные символы для CSV
                        const str = String(field || '');
                        if (str.includes(';') || str.includes('"') || str.includes('\n') || str.includes('\r')) {
                            return `"${str.replace(/"/g, '""')}"`;
                        }
                        return str;
                    })
                    .join(';')
            ),
            '', // Пустая строка для разделения
            footerRow.map(field => {
                const str = String(field || '');
                if (str.includes(';') || str.includes('"') || str.includes('\n') || str.includes('\r')) {
                    return `"${str.replace(/"/g, '""')}"`;
                }
                return str;
            }).join(';')
        ].join('\r\n');

        const blob = new Blob([csvData], { 
            type: 'text/csv; charset=utf-8' 
        });

        return {
            blob,
            filename: `Отчет_по_категориям_скважин_${formattedDateStart}_${project?.name}.csv`,
            isExcel: false
        };
    };

    // Функция для скачивания файла
    const downloadFile = (blob: Blob, filename: string) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.style.display = 'none';
        
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        setTimeout(() => {
            window.URL.revokeObjectURL(url);
        }, 100);
    };

    const handlePrintToExcel = async () => {
        if (!projectUuid) {
            console.error('❌ Не указан projectUuid для экспорта в Excel');
            alert('Ошибка: не указан идентификатор проекта');
            return;
        }

        try {
            console.log('📊 Начало экспорта в Excel для проекта:', projectUuid);
            
            const response = await postWellCategory({ project_uuid: projectUuid }).unwrap();
            
            console.log('✅ Ответ от сервера получен, тип:', typeof response);
            
            const { blob, filename, isExcel } = createBlobFromResponse(response);
            
            downloadFile(blob, filename);
            
            console.log('✅ Файл успешно скачан:', filename);
            
        } catch (err: any) {
            console.error('❌ Ошибка экспорта в Excel:', err);
            
            if (err?.data) {
                console.log('📊 Данные ошибки:', err.data);
                
                try {
                    const { blob, filename } = createBlobFromResponse(err.data);
                    downloadFile(blob, `error_${filename}`);
                } catch (fallbackError) {
                    console.error('❌ Не удалось создать файл из ошибки:', fallbackError);
                }
            }

            try {
                console.log('🔄 Создаем CSV из локальных данных...');
                const { blob, filename } = createCSVFromData();
                downloadFile(blob, filename);
            } catch (fallbackError) {
                console.error('❌ Ошибка при создании fallback файла:', fallbackError);
                alert('Ошибка при экспорте данных');
            }
        }
    };

    const handleClose = () => {
        setRowsMonitoring3([]);
        setTotalWells(0);
        setDataVersion(0);
        handleCloseModal();
    };

    const columnsMonitoring3 = [
        {
            title: 'Категория скважин',
            sortable: 'true',
            accessor: 'one' as const,
        },
        {
            title: 'Пласт',
            sortable: 'true',
            accessor: 'two' as const,
        },
        {
            title: 'Количество скважин',
            sortable: 'true',
            accessor: 'three' as const,
        },
    ];

    return (
        <div>
            <Modal
                isOpen={isOpen}
                hasOverlay
                onClickOutside={handleClose}
                onEsc={handleClose}
                style={{
                    width: '800px',
                    maxWidth: '95vw'
                }}
            >
                <div style={{padding: '20px', display: 'flex', flexDirection: 'column', gap:'20px'}}>
                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                        <Text size='xl' weight='bold'>Мини-отчёт "Сводная таблица по категориям скважин"</Text>
                        <div style={{display: 'flex', gap: '10px', alignItems: 'center'}}>
                            <Button size='s' iconLeft={IconClose} view='clear' onClick={handleClose}/>
                        </div>
                    </div>

                    {isLoading && (
                        <div style={{ 
                            display: 'flex', 
                            justifyContent: 'center', 
                            alignItems: 'center', 
                            padding: '40px' 
                        }}>
                            <Text>Загрузка данных...</Text>
                        </div>
                    )}

                    {!isLoading && isError && (
                        <div style={{ 
                            display: 'flex', 
                            justifyContent: 'center', 
                            alignItems: 'center', 
                            padding: '40px',
                            flexDirection: 'column',
                            gap: '16px'
                        }}>
                            <Text view="warning">Ошибка загрузки данных</Text>
                            <Text size="s" view="secondary">
                                Не удалось загрузить данные по категориям скважин
                            </Text>
                            <Button 
                                size="s"
                                label="Попробовать снова" 
                                onClick={handleRefreshData}
                            />
                        </div>
                    )}

                    {!isLoading && !isError && (
                        <>
                            {rowsMonitoring3.length > 0 ? (
                                <>
                                    <Table
                                        //@ts-ignore
                                        rows={rowsMonitoring3}
                                        //@ts-ignore
                                        columns={columnsMonitoring3}
                                        borderBetweenRows
                                        stickyHeader
                                    />
                                    <div style={{display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '8px'}}>
                                        <Text size='xs' view='secondary'>Всего записей в таблице:</Text>
                                        <Text size='xs' view='secondary'>{rowsMonitoring3.length}</Text>
                                    </div>
                                    <div style={{display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '8px'}}>
                                        <Text size='xs' view='secondary'>Общее количество скважин:</Text>
                                        <Text size='xs' view='secondary'>{totalWells}</Text>
                                    </div>
                                </>
                            ) : (
                                <div style={{ 
                                    display: 'flex', 
                                    justifyContent: 'center', 
                                    alignItems: 'center', 
                                    padding: '40px',
                                    flexDirection: 'column',
                                    gap: '16px'
                                }}>
                                    <Text view="secondary">Нет данных по категориям скважин</Text>
                                    <Button 
                                        size="s"
                                        label="Обновить данные" 
                                        onClick={handleRefreshData}
                                    />
                                </div>
                            )}
                        </>
                    )}

                    <div style={{display: 'flex', justifyContent: 'center', gap: '20px'}}>
                        <Button
                            size="m"
                            view="ghost"
                            label="Назад"
                            width="default"
                            onClick={handleClose}
                            disabled={isPosting}
                        />
                        <Button
                            size="m"
                            view="primary"
                            label={isPosting ? "Формирование файла..." : "Экспорт в файл"}
                            width="default"
                            style={{backgroundColor: 'green'}}
                            onClick={handlePrintToExcel}
                            disabled={!projectUuid || isPosting || rowsMonitoring3.length === 0}
                            loading={isPosting}
                        />
                    </div>
                </div>
            </Modal>
        </div>
    )
}

export default MonitoringStage2